import { Graphics } from 'pixi.js';
import { Boss, type BossDef } from './boss-base';
import { GAME } from '@/config/game';
import type { BulletSystem } from '@/entities/bullets';
import { stasisFriendly } from '@/entities/bullet-fields';
import type { TelegraphSink } from '@/render/telegraphs';

/**
 * B15 — Chronos. A clockwork orb that seeds drifting TIME-WARP zones
 * across the arena: inside a slow field the ship crawls (hard to dodge), inside
 * a haste field it lurches (hard to control). The zones drift and periodically
 * re-seed, so the safe ground keeps moving. docs/02 boss pool.
 * STASIS (docs/02 B1): a clock-face bubble periodically freezes the player's
 * shots around it (they crawl); when it pops they all fly on at once — keep
 * firing and cash the time debt. Its star rings stop dead, then rush (B3).
 * Signatures: time-warp zones + stasis bubble (always), stop-and-go star
 * ring (P1), star spiral (P2/P3).
 */
export const CHRONOS: BossDef = {
  id: 'chronos',
  nameKey: 'boss.chronos',
  tier: 4,
  minRoom: 2,
  baseHp: 1450,
  radius: 48,
  bodyKey: 'boss/chronos/body',
  movement: 'orbit',
  contactDamage: 1,
  phases: [
    {
      hpPct: 1.0,
      patterns: [
        {
          // Stop-and-go: the ring brakes to a halt, hangs, then rushes on
          type: 'radial', bullet: 'bullet/enemy/star', count: 10, interval: 2.8,
          spinDeg: 12, speed: 200, radius: 10, damage: 1, life: 7,
          motion: { accel: -130, minSpeed: 0, rushAt: 2.1, rushAccel: 300, maxSpeed: 260 },
        },
      ],
    },
    {
      hpPct: 0.6,
      patterns: [
        {
          type: 'spiral', bullet: 'bullet/enemy/star', arms: 2, interval: 0.16,
          angularSpeed: 70, speed: 140, radius: 9, damage: 1,
        },
        {
          type: 'aimed', bullet: 'bullet/enemy/ring', count: 3, spreadDeg: 20,
          interval: 2.6, telegraph: 0.4, speed: 240, radius: 10, damage: 2,
          unlockTier: 1,
        },
      ],
    },
    {
      hpPct: 0.3,
      speedMul: 1.25,
      patterns: [
        {
          type: 'spiral', bullet: 'bullet/enemy/star', arms: 3, interval: 0.12,
          angularSpeed: 95, speed: 150, radius: 9, damage: 1,
        },
      ],
    },
  ],
  evolve: [
    {
      // Tick shot: a telegraphed star fan on the second hand
      tier: 2,
      pattern: {
        type: 'aimed', bullet: 'bullet/enemy/star', count: 3, spreadDeg: 18,
        interval: 3.4, telegraph: 0.45, speed: 240, radius: 10, damage: 1,
      },
    },
    {
      // Chime: a slow heavy ring tolls once the casing cracks
      tier: 3,
      fromPhase: 1,
      pattern: {
        type: 'radial', bullet: 'bullet/enemy/ring', count: 12, interval: 4.2,
        spinDeg: 15, speed: 105, radius: 10, damage: 2,
      },
    },
  ],
};

const RESEED_INTERVAL = 9;
const ZONE_DRIFT = 55;
const SLOW_SCALE = 0.5;
const HASTE_SCALE = 1.7;
const SEED_SPREAD = { x: 0.28, y: 0.3 }; // seed offset from home (× arena size)
const ZONE_BOX = 0.42; // zones bounce inside home ± this (× arena size)
const ZONE_INSET = 60; // ...clamped this far inside the real walls (X3)
/** Stasis bubble: off → chime (warn) → on; shots inside crawl at `crawl` */
const STASIS = {
  off: 5.0, warn: 0.6, on: 2.4, radius: 230, crawl: 0.08, ticks: 12,
  rim: 0xf5d08a, fill: 0x4a90d9,
} as const;

interface Zone {
  x: number;
  y: number;
  radius: number;
  scale: number; // <1 slow, >1 haste
  vx: number;
  vy: number;
}

export class ChronosBoss extends Boss {
  private zones: Zone[] = [];
  private reseedTimer = RESEED_INTERVAL;
  private seedAngle = 0.6;
  private readonly zoneG = new Graphics();
  /** Stasis clock: counts down the off time, then runs the bubble */
  private stasis: number = STASIS.off;
  private stasisOn = 0;

  constructor(level: number, x: number, y: number, homeX: number, homeY: number) {
    super(CHRONOS, level, x, y, homeX, homeY);
    this.allowReinforce = false;
    this.view.addChildAt(this.zoneG, 0); // behind the body
    for (let i = 0; i < 3; i++) {
      this.zones.push(this.seedZone(i, { x: 0, y: 0, radius: 0, scale: 1, vx: 0, vy: 0 }));
    }
  }

  /** (Re)seed zone `i` in place — reseeding allocates nothing mid-fight. */
  private seedZone(i: number, z: Zone): Zone {
    this.seedAngle += 2.39996;
    const slow = i !== 2; // two slow fields, one haste
    z.x = this.homeX + Math.cos(this.seedAngle) * this.arenaW * SEED_SPREAD.x;
    z.y = this.homeY + Math.sin(this.seedAngle * 1.3) * this.arenaH * SEED_SPREAD.y;
    z.radius = slow ? 150 : 130;
    z.scale = slow ? SLOW_SCALE : HASTE_SCALE;
    z.vx = Math.cos(this.seedAngle * 0.7) * ZONE_DRIFT;
    z.vy = Math.sin(this.seedAngle * 0.9) * ZONE_DRIFT;
    return z;
  }

  override update(
    dt: number, px: number, py: number, bullets: BulletSystem, telegraphs?: TelegraphSink,
  ): void {
    super.update(dt, px, py, bullets, telegraphs);
    if (this.dead) return;

    // Zones drift inside the REAL arena (X3) — never out past a wall
    const inset = GAME.arena.wallThickness + ZONE_INSET;
    const minX = Math.max(inset, this.homeX - this.arenaW * ZONE_BOX);
    const maxX = Math.min(this.arenaW - inset, this.homeX + this.arenaW * ZONE_BOX);
    const minY = Math.max(inset, this.homeY - this.arenaH * ZONE_BOX);
    const maxY = Math.min(this.arenaH - inset, this.homeY + this.arenaH * ZONE_BOX);
    for (const z of this.zones) {
      z.x += z.vx * dt;
      z.y += z.vy * dt;
      if (z.x < minX || z.x > maxX) z.vx *= -1;
      if (z.y < minY || z.y > maxY) z.vy *= -1;
      z.x = Math.max(minX, Math.min(maxX, z.x));
      z.y = Math.max(minY, Math.min(maxY, z.y));
    }
    this.reseedTimer -= dt * this.intensity;
    if (this.reseedTimer <= 0) {
      this.reseedTimer = RESEED_INTERVAL;
      for (let i = 0; i < this.zones.length; i++) this.seedZone(i, this.zones[i]!);
    }
    this.updateStasis(dt, bullets);
  }

  /** Off → chime → bubble: shots inside crawl until it pops (time debt). */
  private updateStasis(dt: number, bullets: BulletSystem): void {
    if (this.stasisOn > 0) {
      this.stasisOn -= dt;
      stasisFriendly(bullets, this.x, this.y, STASIS.radius, STASIS.crawl);
      return;
    }
    this.stasis -= dt;
    if (this.stasis <= 0) {
      this.stasis = STASIS.off;
      this.stasisOn = STASIS.on;
    }
  }

  private get chiming(): boolean {
    return this.stasisOn <= 0 && this.stasis < STASIS.warn;
  }

  /** Time scale on the player's movement (the fight step feeds envSpeedMul). */
  override playerSpeedMul(px: number, py: number): number {
    if (this.dead) return 1;
    let scale = 1;
    let strongest = 0;
    for (const z of this.zones) {
      if (Math.hypot(px - z.x, py - z.y) < z.radius) {
        const dev = Math.abs(z.scale - 1);
        if (dev > strongest) { strongest = dev; scale = z.scale; }
      }
    }
    return scale;
  }

  override render(alpha: number): void {
    super.render(alpha);
    const vp = this.view.position;
    this.zoneG.clear();
    for (const z of this.zones) {
      const lx = z.x - vp.x;
      const ly = z.y - vp.y;
      const slow = z.scale < 1;
      const color = slow ? 0x4a90d9 : 0xf5a623;
      this.zoneG.circle(lx, ly, z.radius).fill({ color, alpha: 0.12 });
      this.zoneG.circle(lx, ly, z.radius).stroke({ color, width: 3, alpha: 0.5 });
      // Inner clock-tick ring hints at speed up vs slow down
      this.zoneG.circle(lx, ly, z.radius * 0.6).stroke({ color, width: 1.5, alpha: 0.35 });
    }
    this.drawStasis();
    void alpha;
  }

  /** Clock-face bubble: ticks flicker in on the chime, solid while frozen. */
  private drawStasis(): void {
    const on = this.stasisOn > 0;
    if (!on && !(this.chiming && Math.sin(this.stasis * 60) > 0)) return;
    const g = this.zoneG;
    const R = STASIS.radius;
    if (on) g.circle(0, 0, R).fill({ color: STASIS.fill, alpha: 0.1 });
    g.circle(0, 0, R).stroke({ color: STASIS.rim, width: on ? 4 : 2, alpha: on ? 0.75 : 0.5 });
    for (let i = 0; i < STASIS.ticks; i++) {
      const a = (Math.PI * 2 * i) / STASIS.ticks;
      g.moveTo(Math.cos(a) * R * 0.9, Math.sin(a) * R * 0.9)
        .lineTo(Math.cos(a) * R, Math.sin(a) * R)
        .stroke({ color: STASIS.rim, width: 3, alpha: on ? 0.8 : 0.5 });
    }
  }
}
